﻿import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { useParams, Redirect } from 'react-router';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import ColourPaper from '../Utilities/ColourPaper';
import Alert from '@material-ui/lab/Alert';
import Bugsnag from '@bugsnag/js';

interface RouteParams {
    userId: string;
    code: string;
}

export default function RegisterConfirmation() {
    const { userId, code } = useParams<RouteParams>();

    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [redirect, setRedirect] = React.useState(false);
    const [exists, setExists] = React.useState(false);

    React.useEffect(() => {
        fetch('User/CheckUserExists?id=' + userId, {
            headers: { 'Content-Type': 'application/json; charset=utf-8' },
        })
            .then(response => response.json())
            .then(data => {
                setExists(data);
            }).catch(error => {
                Bugsnag.notify(error);
            });
    }, []);

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(event.target.value);
    }

    const onConfirmChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(event.target.value);
    }

    const createUser = async () => {
        fetch('User/SetPassword', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json; charset=utf-8' },
            body: JSON.stringify({ userId: userId, code: code, password: password })
        })
            .then(response => response.text())
            .then(data => {
                if (data === 'Ok') {
                    setRedirect(true);
                }
            }).catch(error => {
                Bugsnag.notify(error);
            });
    }

    ValidatorForm.addValidationRule('isPasswordMatch', value => {
        if (value !== password) {
            return false;
        }
        return true;
    });

    ValidatorForm.addValidationRule('hasUppercase', value => {
        if (/^(?=.*[A-Z]).{1,}$/.test(value)) {
            return true;
        }
        return false;
    });

    ValidatorForm.addValidationRule('hasLowercase', value => {
        if (/^(?=.*[a-z]).{1,}$/.test(value)) {
            return true;
        }
        return false;
    });

    ValidatorForm.addValidationRule('hasNumber', value => {
        if (/^(?=.*[0-9]).{1,}$/.test(value)) {
            return true;
        }
        return false;
    });

    ValidatorForm.addValidationRule('hasSpecialChar', value => {
        if (/^(?=.*[\W]).{1,}$/.test(value)) {
            return true;
        }
        return false;
    });

    ValidatorForm.addValidationRule('noWhitespace', value => {
        if (/^\S*$/.test(value)) {
            return true;
        }
        return false;
    });

    return (
        <React.Fragment>
            {
                redirect ? <Redirect to="/" />
                    :
                    <React.Fragment>
                        {exists ?
                            <ValidatorForm onSubmit={createUser}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <ColourPaper>
                                            <Typography variant="h2">Create Password</Typography>
                                            <Grid container>
                                                <Grid item xs={12}>
                                                    <TextValidator
                                                        fullWidth
                                                        variant="outlined"
                                                        margin="normal"
                                                        label="Password"
                                                        name="password"
                                                        value={password}
                                                        type="password"
                                                        validators={['required', 'minStringLength:  6', 'noWhitespace', 'hasUppercase', 'hasLowercase', 'hasNumber', 'hasSpecialChar']}
                                                        errorMessages={['This field is required', 'Must be at least 6 characters', 'No whitespace', 'Must contain at least 1 uppercase letter', 'Must contain at least 1 lowercase letter', 'Must contain at least 1 number', 'Must contain at least 1 special character']}
                                                        onChange={onChange}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextValidator
                                                        fullWidth
                                                        variant="outlined"
                                                        margin="normal"
                                                        label="Confirm Password"
                                                        name="confirmPassword"
                                                        value={confirmPassword}
                                                        type="password"
                                                        validators={['isPasswordMatch']}
                                                        errorMessages={["Password doesn't match"]}
                                                        onChange={onConfirmChange}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Button fullWidth variant="contained" color="primary" type="submit">Confirm</Button>
                                                </Grid>
                                            </Grid>
                                        </ColourPaper>
                                    </Grid>
                                </Grid>
                            </ValidatorForm>
                            :
                            <Alert severity="warning">Invalid link, user does not exist.</Alert>
                        }
                    </React.Fragment>
            }
        </React.Fragment>
    );
}